import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import {
  inputClass,
  labelClass,
  inlineErrorClass,
  inlineSuccessClass,
} from '@/components/ui/inputStyles';
import {
  AI_PROVIDER_OPTIONS,
  aiProviderLabel,
  type AiProviderId,
  type UserAiProfile,
} from '@/lib/aiEngine';
import { cn } from '@/lib/cn';

type ListResp = {
  ok: boolean;
  profiles?: UserAiProfile[];
  default_profile_id?: number;
  max_profiles?: number;
  count?: number;
};

const emptyForm = () => ({
  id: 0,
  label: '',
  ai_provider: 'deepseek' as AiProviderId,
  ai_model: '',
  ai_api_base: '',
  ai_api_key: '',
  set_as_default: true,
});

export function AiEnginesPage() {
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<UserAiProfile[]>([]);
  const [maxProfiles, setMaxProfiles] = useState(10);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [keyWasSet, setKeyWasSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState('');
  const [testErr, setTestErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<ListResp>('list_user_ai_profiles', { method: 'GET' });
      if (!r.ok) {
        setErr('加载失败');
        return;
      }
      setProfiles(r.profiles ?? []);
      setMaxProfiles(r.max_profiles ?? 10);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setForm({ ...emptyForm(), set_as_default: profiles.length === 0 });
    setKeyWasSet(false);
    setTestMsg('');
    setTestErr('');
    setEditorOpen(true);
  }

  function openEdit(p: UserAiProfile) {
    setForm({
      id: p.id,
      label: p.label,
      ai_provider: (p.ai_provider as AiProviderId) || 'deepseek',
      ai_model: p.ai_model,
      ai_api_base: p.ai_api_base,
      ai_api_key: '',
      set_as_default: p.is_default === true,
    });
    setKeyWasSet(p.ai_api_key_set);
    setTestMsg('');
    setTestErr('');
    setEditorOpen(true);
  }

  async function runTest() {
    setTesting(true);
    setTestMsg('');
    setTestErr('');
    try {
      const body: Record<string, unknown> = {
        ai_provider: form.ai_provider,
        ai_model: form.ai_model.trim(),
        ai_api_base: form.ai_api_base.trim(),
      };
      if (form.id > 0) body.id = form.id;
      if (form.ai_api_key.trim()) body.ai_api_key = form.ai_api_key.trim();
      const r = await apiFetch<{ ok: boolean; message?: string; detail?: string; err?: string }>(
        'test_user_ai_profile',
        { method: 'POST', body: JSON.stringify(body) }
      );
      if (r.ok) {
        setTestMsg(r.message ?? '测试通过');
      } else {
        setTestErr(r.detail ?? r.err ?? '测试失败');
      }
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : '测试失败');
    } finally {
      setTesting(false);
    }
  }

  async function saveProfile() {
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const body: Record<string, unknown> = {
        id: form.id > 0 ? form.id : undefined,
        label: form.label.trim(),
        ai_provider: form.ai_provider,
        ai_model: form.ai_model.trim(),
        ai_api_base: form.ai_api_base.trim(),
        set_as_default: form.set_as_default,
      };
      if (form.ai_api_key.trim()) body.ai_api_key = form.ai_api_key.trim();
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('save_user_ai_profile', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        setErr(r.detail ?? r.err ?? '保存失败');
        return;
      }
      setEditorOpen(false);
      setMsg('已保存');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function setDefault(id: number) {
    try {
      await apiFetch('set_default_user_ai_profile', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      setMsg('已设为默认');
      await load();
    } catch {
      setErr('设置默认失败');
    }
  }

  async function removeProfile(id: number) {
    if (!window.confirm('删除后，绑定该配置的站点将回退为「账号默认 AI」。确定删除？')) return;
    try {
      await apiFetch('delete_user_ai_profile', {
        method: 'POST',
        body: JSON.stringify({ id }),
      });
      setMsg('已删除');
      await load();
    } catch {
      setErr('删除失败');
    }
  }

  const atLimit = profiles.length >= maxProfiles;
  const providerOpt = AI_PROVIDER_OPTIONS.find((o) => o.id === form.ai_provider);

  if (loading) {
    return <Spinner label="加载 AI 配置" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI 接口引擎"
        description="在账号下统一配置大模型 API，所有站点共用；各站点可在「接入与推送」或创建站点时选择使用哪一套。"
      />
      <PcRecommendedCallout>API Key 仅保存在服务端，不会在浏览器长期明文显示。</PcRecommendedCallout>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          已配置 <strong className="text-slate-900">{profiles.length}</strong> / {maxProfiles} 个
        </p>
        <Button type="button" variant="primary" disabled={atLimit} onClick={openCreate}>
          {atLimit ? '已达上限' : '新建 AI 配置'}
        </Button>
      </div>

      {profiles.length === 0 ? (
        <Card title="尚未配置">
          <p className="mb-4 text-sm text-slate-600">
            配置后可在「内容与样式」中使用 AI 写标题，并在各站点绑定不同模型（默认使用账号默认项）。
          </p>
          <Button type="button" variant="primary" onClick={openCreate}>
            立即配置
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {profiles.map((p) => (
            <div
              key={p.id}
              className={cn(
                'flex flex-col rounded-2xl border bg-white p-5 shadow-sm shadow-slate-900/[0.04] transition-shadow hover:shadow-md',
                p.is_default ? 'border-teal-300 ring-1 ring-teal-200/80' : 'border-slate-200/90'
              )}
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{p.label}</h3>
                  <p className="mt-0.5 text-xs text-slate-500">{aiProviderLabel(String(p.ai_provider))}</p>
                </div>
                {p.is_default ? (
                  <span className="shrink-0 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-semibold text-teal-800">
                    默认
                  </span>
                ) : null}
              </div>
              <dl className="mb-4 flex-1 space-y-1 text-xs text-slate-600">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-slate-400">模型</dt>
                  <dd className="font-mono break-all">{p.ai_model || '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-slate-400">API 根</dt>
                  <dd className="font-mono break-all">{p.ai_api_base || '（厂商默认）'}</dd>
                </div>
                <div>
                  <span className={p.ai_api_key_set ? 'text-emerald-700' : 'text-amber-700'}>
                    {p.ai_api_key_set ? '已配置 Key' : '未配置 Key'}
                  </span>
                </div>
              </dl>
              <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(p)}>
                  编辑
                </Button>
                {!p.is_default ? (
                  <Button type="button" variant="secondary" size="sm" onClick={() => void setDefault(p.id)}>
                    设为默认
                  </Button>
                ) : null}
                <Button type="button" variant="secondary" size="sm" onClick={() => void removeProfile(p.id)}>
                  删除
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}
      {err ? <p className={inlineErrorClass}>{err}</p> : null}

      <Modal
        open={editorOpen}
        title={form.id > 0 ? '编辑 AI 配置' : '新建 AI 配置'}
        onClose={() => !saving && !testing && setEditorOpen(false)}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" type="button" disabled={saving || testing} onClick={() => setEditorOpen(false)}>
              取消
            </Button>
            <Button type="button" variant="secondary" disabled={testing || saving} onClick={() => void runTest()}>
              {testing ? '测试中…' : '测试连接'}
            </Button>
            <Button type="button" variant="primary" disabled={saving || testing} onClick={() => void saveProfile()}>
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <label className={labelClass}>
            <span>显示名称</span>
            <input
              className={inputClass}
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              placeholder="如：DeepSeek 主力"
              maxLength={64}
            />
          </label>
          <label className={labelClass}>
            <span>厂商</span>
            <select
              className={inputClass}
              value={form.ai_provider}
              onChange={(e) => setForm((f) => ({ ...f, ai_provider: e.target.value as AiProviderId }))}
            >
              {AI_PROVIDER_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            <span>模型名 / Endpoint ID</span>
            <input
              className={inputClass + ' font-mono text-xs'}
              value={form.ai_model}
              onChange={(e) => setForm((f) => ({ ...f, ai_model: e.target.value }))}
              placeholder={providerOpt?.modelHint}
            />
          </label>
          <label className={labelClass}>
            <span>API 根地址（可选）</span>
            <input
              className={inputClass + ' font-mono text-xs'}
              value={form.ai_api_base}
              onChange={(e) => setForm((f) => ({ ...f, ai_api_base: e.target.value }))}
              placeholder={providerOpt?.baseHint ?? '留空使用厂商默认'}
            />
          </label>
          <label className={labelClass}>
            <span>API Key</span>
            <input
              type="password"
              className={inputClass + ' font-mono text-xs'}
              value={form.ai_api_key}
              onChange={(e) => setForm((f) => ({ ...f, ai_api_key: e.target.value }))}
              placeholder={keyWasSet ? '已保存，留空不修改' : '必填'}
              autoComplete="off"
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.set_as_default}
              onChange={(e) => setForm((f) => ({ ...f, set_as_default: e.target.checked }))}
            />
            保存后设为账号默认 AI
          </label>
          {testMsg ? <p className={inlineSuccessClass}>{testMsg}</p> : null}
          {testErr ? <p className={inlineErrorClass}>{testErr}</p> : null}
        </div>
      </Modal>
    </div>
  );
}
